import i18n, { type BackendModule, type ResourceKey } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { type DateTime, Settings } from "luxon";
import { initReactI18next } from "react-i18next";
import { ERROR_SEVERITY_PROPERTIES, trackErrorEvent } from "../utils/analytics";
import { moduleUnlessReloading } from "../utils/chunkReload";
import translationsEN from "./en/en.json";

export const SUPPORTED_LANGUAGES = ["en", "es", "he"] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const isSupportedLanguage = (value: string): value is SupportedLanguage => SUPPORTED_LANGUAGES.some((language) => language === value);

/** The query parameter that links a page in a given language; I18nProvider saves it to the account. */
export const LANGUAGE_PARAM = "lang";

const LANGUAGE_PARAM_DETECTOR = "languageParam";

// Read at startup too, so a linked Spanish or Hebrew page waits for its language behind the loading screen
// instead of showing English until I18nProvider applies the parameter. Only exact codes, as I18nProvider
// accepts, or the two would disagree on something like `es-MX`.
const languageDetector = new LanguageDetector();
languageDetector.addDetector({
	name: LANGUAGE_PARAM_DETECTOR,
	lookup: () => {
		const linkedLanguage = new URLSearchParams(window.location.search).get(LANGUAGE_PARAM);
		return linkedLanguage !== null && isSupportedLanguage(linkedLanguage) ? linkedLanguage : undefined;
	},
});

// English ships with the app because every missing key falls back to it and nearly every user reads it;
// each other language (over 1 MB) downloads only for the users who choose it.
const DOWNLOADED_TRANSLATIONS: Record<Exclude<SupportedLanguage, "en">, () => Promise<{ default: ResourceKey }>> = {
	es: () => import("./es/es.json"),
	he: () => import("./he/he.json"),
};

const isDownloadedLanguage = (language: string): language is keyof typeof DOWNLOADED_TRANSLATIONS => Object.hasOwn(DOWNLOADED_TRANSLATIONS, language);

const downloadedTranslationsBackend: BackendModule = {
	type: "backend",
	init: () => {},
	read: (language, _namespace, callback) => {
		if (!isDownloadedLanguage(language)) {
			callback(new Error(`No translations to download for "${language}"`), false);
			return;
		}
		DOWNLOADED_TRANSLATIONS[language]()
			.then(moduleUnlessReloading)
			.then((translationsModule) => translationsModule.default)
			// Two handlers, not a trailing catch: an error thrown by i18next's own listeners inside the success
			// callback must not be reported back to it as a failed download.
			.then(
				(translations) => callback(null, translations),
				(error: unknown) => callback(error instanceof Error ? error : new Error(String(error)), false),
			);
	},
};

// DEFAULT: i18next shows English in place of a language that failed to download, so the page stays usable;
// without this report the failure would be silent.
i18n.on("failedLoading", (language: string, namespace: string, error: unknown) => {
	trackErrorEvent("translation_load_failed", error, { language, namespace, ...ERROR_SEVERITY_PROPERTIES });
});

i18n
	.use(initReactI18next)
	.use(languageDetector)
	.use(downloadedTranslationsBackend)
	.init({
		resources: { en: { translation: translationsEN } },
		partialBundledLanguages: true,
		supportedLngs: SUPPORTED_LANGUAGES,
		// A regional code such as es-MX reads the es translations instead of requesting its own.
		load: "languageOnly",
		// Nothing in the app sets the detector's cookie or session storage, so only what it does use is read.
		detection: { order: [LANGUAGE_PARAM_DETECTOR, "localStorage", "navigator", "htmlTag"] },
		fallbackLng: "en",
		keySeparator: false,
		interpolation: {
			escapeValue: false,
		},
	});

/**
 * Luxon locale handling
 * - Luxon uses Intl under the hood
 * - No updateLocale equivalent; formatting comes from locale data
 */
const localeMap: Record<string, string> = {
	en: "en-US",
	es: "es-US",
	he: "he-US",
};

// Sync Luxon with i18n
i18n.on("languageChanged", (lng) => {
	Settings.defaultLocale = localeMap[lng] ?? "en-US";
});

// Initial locale
Settings.defaultLocale = localeMap[i18n.language] ?? "en-US";

/**
 * Optional helpers if you relied on custom short names in Dayjs
 * (Luxon does not support mutating locale data)
 */
export const formatWeekdayShort = (dt: DateTime) => dt.setLocale(Settings.defaultLocale).toFormat("ccc");

export const formatMonthShort = (dt: DateTime) => dt.setLocale(Settings.defaultLocale).toFormat("LLL");

export default i18n;
